import { Geotab, LogRecord } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_LOG_RECORD } from "@/constants/resultsLimits";

type LogRecordGetter = Pick<Geotab, "get">;

/**
 * Fetches all LogRecords of a device in a range with plain `Get` calls. Geotab returns records
 * oldest first and cuts at the limit, so a full page is followed by another `Get` starting just
 * after its last record. Uses `Get`, not `GetFeed`: a feed ignores `deviceSearch`, `fromDate` and
 * `toDate` for LogRecord and walks the whole database instead (Geotab data feed guide).
 */
export async function fetchLogRecordsByRange(
  client: LogRecordGetter,
  deviceId: string,
  range: { from: Date; to: Date },
  signal?: AbortSignal,
  pageLimit = RESULTS_LIMIT_LOG_RECORD,
): Promise<LogRecord[]> {
  const all: LogRecord[] = [];
  let from = range.from;

  for (;;) {
    const page = (await client.get(
      "LogRecord",
      { deviceSearch: { id: deviceId }, fromDate: from, toDate: range.to },
      pageLimit,
      signal,
    )) as LogRecord[];
    all.push(...page);
    if (page.length < pageLimit) return all;

    const lastTime = new Date(page[page.length - 1].dateTime as string | Date).getTime();
    const next = new Date(lastTime + 1);
    // A full page that cannot be continued would silently cut the route short; fail instead,
    // like fetchStatusDataComplete does.
    if (Number.isNaN(lastTime) || next.getTime() <= from.getTime()) {
      throw new Error(`LogRecord paging for ${deviceId} cannot advance past ${from.toISOString()}`);
    }
    if (next.getTime() > range.to.getTime()) return all;
    from = next;
  }
}

/** Key and fetcher shared by {@link useLogRecordsByRange} and callers that query several devices at once. */
export function logRecordsByRangeQuery(
  geotabClient: LogRecordGetter,
  activeSessionId: string | null | undefined,
  deviceId: string,
  fromDate: Date,
  toDate: Date,
) {
  return {
    queryKey: ["logRecordsByRange", activeSessionId, deviceId, fromDate, toDate],
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchLogRecordsByRange(geotabClient, deviceId, { from: fromDate, to: toDate }, signal),
  };
}

/** Positions of one device inside a fixed time window, loaded once and complete past 50k records. */
export function useLogRecordsByRange(
  deviceId: string | undefined,
  fromDate: Date,
  toDate: Date,
  enabled = true,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery<LogRecord[]>({
    ...logRecordsByRangeQuery(geotabClient, activeSessionId, deviceId ?? "", fromDate, toDate),
    enabled: enabled && !!deviceId,
  });
}
